import {
  ChangeDetectionStrategy,
  Component,
  effect,
  model,
  input,
  computed,
  output,
  signal,
} from '@angular/core';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { DropdownComponent } from '@bizzdesign/ui-dropdown';
import {
  type AuthenticatedUser,
  type BDTreeNode,
  type DropdownSettings,
  type SystemAttributesFilter,
  type SystemAttributesUser,
} from '@bizzdesign/shared/models';
import { systemAttributesUserToUserTreeNodeOptions } from '@bizzdesign/data-access-search';
import { isNotUndefinedOrNull } from '@bizzdesign/bizzdesign-sdk';

@Component({
  selector: 'bd-advanced-filter-people',
  imports: [SharedI18nModule, DropdownComponent],
  templateUrl: './advanced-filter-people.component.html',
  styleUrl: './advanced-filter-people.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancedFilterPeopleComponent {
  users = input<SystemAttributesUser[]>([]);
  usersLoading = input<boolean>(false);
  selectedFilter = input<SystemAttributesFilter | undefined>();
  authenticatedUser = input<AuthenticatedUser | undefined>();
  filterChange = output<SystemAttributesFilter | undefined>();
  searchUsers = output<string>();

  selectedUser = model<BDTreeNode<SystemAttributesUser> | undefined>();
  filterTerm = signal<string>('');

  dropdownSettings = computed(
    (): DropdownSettings => ({
      selectionMode: 'single',
      inputType: 'default',
      layout: 'list',
      dropdownWidth: '300px',
      tScope: 'featureSearch',
      inputPlaceholderKey: 'peopleFilter.userSelectPlaceholder',
      filterPlaceholderKey: 'peopleFilter.userSearchPlaceholder',
      filterOptionsCount: 0,
      clearOption: true,
      skipDefaultFilter: true,
      emptyStateMessageKey: this.filterTerm()
        ? 'peopleFilter.emptyStateMessage'
        : 'peopleFilter.emptyStateMessageWithoutTerm',
      dropdownLabelKey: 'peopleFilter.createdBy',
    }),
  );

  userOptions = computed<BDTreeNode<SystemAttributesUser>[]>(() =>
    systemAttributesUserToUserTreeNodeOptions(
      this.users(),
      this.authenticatedUser(),
    ),
  );

  constructor() {
    effect(() => {
      if (isNotUndefinedOrNull(this.selectedFilter()?.createdBy)) {
        this.setSelectedUser();
      } else {
        this.removeSelectedUser();
      }
    });
  }

  removeSelectedUser() {
    this.selectedUser.set(undefined);
  }

  onFilterUsers(term: string) {
    this.filterTerm.set(term);
    this.searchUsers.emit(term);
  }

  setSelectedUser() {
    const systemAttributesUser = this.selectedFilter()?.createdBy;
    if (systemAttributesUser) {
      this.selectedUser.set({
        key: systemAttributesUser.userId,
        data: systemAttributesUser,
        label:
          systemAttributesUser.firstName + ' ' + systemAttributesUser.lastName,
        systemAttributesUser: systemAttributesUser,
      });
    }
  }

  onSelectUser(user: BDTreeNode<SystemAttributesUser>) {
    this.filterChange.emit({
      createdBy: user.systemAttributesUser!,
    });
  }

  onUnselectUser() {
    this.removeSelectedUser();
    this.filterChange.emit(undefined);
  }
}
